/**
 * Create a debounced version of a given function
 * @param {Function} callback Function to be debounced
 * @param {number} wait Time to wait in miliseconds
 */

type ArgumentTypes<F> = F extends (...args: infer A) => unknown ? A : never;

// eslint-disable-next-line @typescript-eslint/ban-types
export function debounce<F extends Function>(callback: F, wait: number) {
  let timeout: ReturnType<typeof setTimeout>;
  return function retFunction(...args: ArgumentTypes<F>) {
    function functionCall() {
      return callback(...args);
    }
    clearTimeout(timeout);
    timeout = setTimeout(functionCall, wait);
  };
}

/**
 * Returns comma separated string of an object's values
 * @param  obj
 */
export const getCommaSeparatedObjectValues = (obj: object | null | undefined) =>
  Object.values(obj ?? {}).join(', ');

/**
 * Checks if the environment is in development mode
 */
export const isDev = () => process.env['NODE_ENV'] === 'development';

/**
 * Checks if a string|array|object is empty
 * @param item A value to check
 */
export const isEmpty = (
  item: string | [] | object | null | undefined
): boolean => {
  if (item === null || item === undefined) {
    return true;
  }

  if (Array.isArray(item) || typeof item === 'string') {
    return item.length === 0;
  }

  if (Object.prototype.toString.call(item) === '[object Date]') {
    return false;
  }

  if (typeof item === 'object') {
    return Object.getOwnPropertyNames(item).length === 0;
  }

  return false;
};

export const delay = (timeout: number) =>
  new Promise((resolve) => setTimeout(resolve, timeout));

/**
 * sorts a list of items by a specified property
 * @param list - the list of items to be sorted
 * @param property - the property name to sort by
 * @returns { Array } - the sorted list
 */
export const sortListByAttributeDescending = (list, property) => {
  return list.sort((a, b) => {
    if (a[property] < b[property]) {
      return 1;
    }
    if (a[property] > b[property]) {
      return -1;
    }
    return 0;
  });
};

export const capitalizeText = (text: string) => {
  return text
    .toLowerCase()
    .replace(/(\b[a-z](?!\s))/g, (letter) => letter.toUpperCase());
};

export const sortData = (items, sortBy) => {
  return items.sort((a, b) =>
    a[sortBy]?.toLowerCase().localeCompare(b[sortBy]?.toLowerCase())
  );
};

export const isTruthyDefault = (value: string | boolean) =>
  value === true || value === 'true';
